import type { DayBundle, MonthBundle, PublicEvent } from "../types/public";
import { normalizeKarinIcon, publicPerformersList } from "../types/public";

function decodeHtmlEntities(value: string): string {
  const named: Record<string, string> = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: "\"",
    apos: "'",
    nbsp: " ",
    ensp: " ",
    emsp: " ",
    thinsp: " ",
    ndash: "-",
    mdash: "-",
    hellip: "…",
  };
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]+);/gi, (match, entity: string) => {
    const e = String(entity).toLowerCase();
    if (e.startsWith("#x")) {
      const code = Number.parseInt(e.slice(2), 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    if (e.startsWith("#")) {
      const code = Number.parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    return named[e] ?? match;
  });
}

/** KV / 手編集 JSON の揺れでも表サイトが落ちないよう1件を正規化 */
export function coercePublicEvent(raw: unknown): PublicEvent | null {
  if (raw == null || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = r.id != null && String(r.id).trim() ? String(r.id).trim() : null;
  if (!id) return null;
  const str = (v: unknown): string | null => (v == null || v === "" ? null : decodeHtmlEntities(String(v)));
  const perf = publicPerformersList({ performers: r.performers as never });
  return {
    id,
    source: String(r.source ?? ""),
    event_kind: String(r.event_kind ?? "event"),
    event_name: decodeHtmlEntities(String(r.event_name ?? "（無題）")),
    venue: str(r.venue),
    prefecture: str(r.prefecture),
    event_date: String(r.event_date ?? "").trim().slice(0, 32) || "1970-01-01",
    time_start: str(r.time_start),
    time_end: str(r.time_end),
    door_time: str(r.door_time),
    curtain_time: str(r.curtain_time),
    door_time_2: str(r.door_time_2),
    curtain_time_2: str(r.curtain_time_2),
    door_time_3: str(r.door_time_3),
    curtain_time_3: str(r.curtain_time_3),
    performers: perf,
    description: str(r.description),
    detail_url: str(r.detail_url),
    reserve_url: str(r.reserve_url),
    memo: str(r.memo),
    fan_group_name: str(r.fan_group_name),
    fan_artist_name: str(r.fan_artist_name),
    source_title: str(r.source_title),
    fan_submitter_key_hash: str(r.fan_submitter_key_hash),
    performance_count: r.performance_count == null || r.performance_count === "" ? null : Number(r.performance_count),
    is_karin: Boolean(r.is_karin),
    karin_subtype: str(r.karin_subtype),
    karin_icon: normalizeKarinIcon(r.karin_icon),
    group_code: str(r.group_code),
    reception_start: str(r.reception_start),
    reception_end: str(r.reception_end),
    performance_text: r.performance_text == null || r.performance_text === "" ? null : String(r.performance_text),
  };
}

export function normalizeMonthBundle(data: unknown): MonthBundle | null {
  if (data == null || typeof data !== "object") return null;
  const o = data as Record<string, unknown>;
  const month = String(o.month ?? "");
  const generatedAt = String(o.generatedAt ?? "");
  const eventsIn = Array.isArray(o.events) ? o.events : [];
  const events = eventsIn.map(coercePublicEvent).filter((e): e is PublicEvent => e != null);
  const byDayRaw = o.byDay != null && typeof o.byDay === "object" ? (o.byDay as Record<string, unknown>) : {};
  const byDay: MonthBundle["byDay"] = {};
  for (const [day, v] of Object.entries(byDayRaw)) {
    if (!v || typeof v !== "object") continue;
    const b = v as Record<string, unknown>;
    const karin = (Array.isArray(b.karin) ? b.karin : []).map(coercePublicEvent).filter((e): e is PublicEvent => e != null);
    const others = (Array.isArray(b.others) ? b.others : []).map(coercePublicEvent).filter((e): e is PublicEvent => e != null);
    byDay[day] = { karin, others };
  }
  return { month, generatedAt, events, byDay };
}

export function normalizeDayBundle(data: unknown): DayBundle | null {
  if (data == null || typeof data !== "object") return null;
  const o = data as Record<string, unknown>;
  const eventsIn = Array.isArray(o.events) ? o.events : [];
  const events = eventsIn.map(coercePublicEvent).filter((e): e is PublicEvent => e != null);
  return {
    date: String(o.date ?? ""),
    generatedAt: String(o.generatedAt ?? ""),
    events,
  };
}
